import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Tabs,
  Tab,
  Divider,
  InputAdornment,
  Alert,
  Chip,
  Link,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Stack,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonIcon from "@mui/icons-material/Person";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  STANDARD_DEDUCTIONS_2025,
  STATE_NAMES,
  calculateIncomeDetails,
  generateTaxProfileId,
  loadTaxProfiles,
  TAX_PROFILES_STORAGE_KEY,
  IncomeDetails,
  IncomeInfo,
  IncomeSourceEntry,
  FilingStatus,
  StateCode,
  TaxProfile,
} from "../../lib/taxCalculatorUtils";
import PageShareModal from "../common/PageShareModal";
import { pageShareAPI } from "../../services/api/api";
import { useShareAction } from "../../contexts/ShareActionContext";
import { taxProfilesDB } from "../../services/database/taxProfilesDB";

function formatCurrency(amount: number): string {
  const isNegative = amount < 0;
  const formatted = Math.abs(amount).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return isNegative ? `-${formatted}` : formatted;
}

function formatPercent(rate: number): string {
  return `${(rate * 100).toFixed(2)}%`;
}

const generateIncomeSourceId = (): string =>
  `income_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const normalizePaymentType = (value: any): "hourly" | "salary" =>
  value === "hourly" ? "hourly" : "salary";

const normalizeIncomeSources = (raw: any): IncomeSourceEntry[] => {
  if (Array.isArray(raw?.incomeSources) && raw.incomeSources.length > 0) {
    return raw.incomeSources.map((source: any) => ({
      id: typeof source?.id === "string" ? source.id : generateIncomeSourceId(),
      revenueSource:
        typeof source?.revenueSource === "string" ? source.revenueSource : "",
      paymentType: normalizePaymentType(source?.paymentType),
      paymentAmount:
        typeof source?.paymentAmount === "number" &&
        Number.isFinite(source.paymentAmount)
          ? source.paymentAmount
          : 0,
      hoursWorkedWeekly:
        typeof source?.hoursWorkedWeekly === "number" &&
        Number.isFinite(source.hoursWorkedWeekly)
          ? source.hoursWorkedWeekly
          : 40,
    }));
  }

  return [
    {
      id: generateIncomeSourceId(),
      revenueSource: "",
      paymentType: normalizePaymentType(raw?.paymentType),
      paymentAmount:
        typeof raw?.paymentAmount === "number" &&
        Number.isFinite(raw.paymentAmount)
          ? raw.paymentAmount
          : 0,
      hoursWorkedWeekly:
        typeof raw?.hoursWorkedWeekly === "number" &&
        Number.isFinite(raw.hoursWorkedWeekly)
          ? raw.hoursWorkedWeekly
          : 40,
    },
  ];
};

const normalizeIncomeInfo = (raw: any): IncomeInfo => {
  const incomeSource =
    raw?.incomeSource === "self_employed" ? "self_employed" : "w2";
  const filingStatus: FilingStatus =
    raw?.filingStatus === "married_jointly"
      ? "married_jointly"
      : raw?.filingStatus === "head_of_household"
        ? "head_of_household"
        : "single";
  const state: StateCode = raw?.state === "NY" ? "NY" : "OR";

  return {
    incomeSource,
    incomeSources: normalizeIncomeSources(raw),
    businessDeductions:
      typeof raw?.businessDeductions === "number" &&
      Number.isFinite(raw.businessDeductions)
        ? raw.businessDeductions
        : 0,
    state,
    filingStatus,
    numChildren:
      typeof raw?.numChildren === "number" && Number.isFinite(raw.numChildren)
        ? Math.max(0, raw.numChildren)
        : 0,
  };
};

// Results Tab Component
interface ResultsTabProps {
  details: IncomeDetails;
  stateName: string;
  numChildren: number;
  period: "weekly" | "monthly" | "yearly";
}

const ResultsTab: React.FC<ResultsTabProps> = ({
  details,
  stateName,
  numChildren,
  period,
}) => {
  const [paymentPlanExpanded, setPaymentPlanExpanded] = useState(false);

  const {
    grossIncome,
    businessDeductions,
    netBusinessIncome,
    selfEmploymentTaxDeduction,
    standardDeduction,
    taxableIncome,
    totalTax,
    netIncome,
    taxBreakdown,
    childTaxCredit,
    federalMarginalRate,
    stateMarginalRate,
    effectiveTaxRate,
    isSelfEmployed,
  } = details;

  const federalTaxes = isSelfEmployed
    ? [
        {
          name: "Federal Income Tax",
          amount: taxBreakdown.federal_income_tax,
          rate: federalMarginalRate,
          showRate: true,
          tooltip: null,
        },
        {
          name: "SE Medicare Tax",
          amount: taxBreakdown.medicare_tax,
          rate: 0.029, // 2.9% for self-employed (employee + employer portions)
          showRate: true,
          tooltip:
            "Self-employment Medicare tax is 2.9% (you pay both the 1.45% employee and 1.45% employer portions). All net self-employment income is subject to this tax.",
        },
        {
          name: "SE Social Security Tax",
          amount: taxBreakdown.social_security_tax,
          rate: 0.124, // 12.4% for self-employed (employee + employer portions)
          showRate: true,
          tooltip:
            "Self-employment Social Security tax is 12.4% (you pay both the 6.2% employee and 6.2% employer portions) on net earnings up to $176,100 (2025).",
        },
      ]
    : [
        {
          name: "Federal Income Tax",
          amount: taxBreakdown.federal_income_tax,
          rate: federalMarginalRate,
          showRate: true,
          tooltip: null,
        },
        {
          name: "Medicare Tax",
          amount: taxBreakdown.medicare_tax,
          rate: 0.0145,
          showRate: true,
          tooltip:
            "Medicare tax funds healthcare for people 65+ and those with disabilities. All wages are subject to this 1.45% tax (2.9% total split between employee and employer).",
        },
        {
          name: "Social Security Tax",
          amount: taxBreakdown.social_security_tax,
          rate: 0.062,
          showRate: true,
          tooltip:
            "Social Security tax funds retirement, disability, and survivor benefits. You pay 6.2% on wages up to $176,100 (2025). Your employer matches this amount.",
        },
      ];

  const stateTaxes = [
    {
      name: `${stateName} Income Tax`,
      amount: taxBreakdown.state_income_tax,
      rate: stateMarginalRate,
      showRate: true,
      tooltip: null,
    },
  ];

  const totalFederalTaxes = federalTaxes.reduce((sum, t) => sum + t.amount, 0);
  const totalStateTaxes = stateTaxes.reduce((sum, t) => sum + t.amount, 0);

  // Calculate effective rates for each tax line
  const calcEffectiveRate = (amount: number) =>
    grossIncome > 0 ? amount / grossIncome : 0;

  const periodDivisor =
    period === "weekly" ? 52 : period === "monthly" ? 12 : 1;
  const periodStandardDeduction = standardDeduction / periodDivisor;
  const periodChildTaxCredit = childTaxCredit / periodDivisor;
  const periodBusinessDeductions = businessDeductions / periodDivisor;
  const periodNetBusinessIncome = netBusinessIncome / periodDivisor;
  const periodSETaxDeduction = selfEmploymentTaxDeduction / periodDivisor;

  return (
    <Box sx={{ pt: 2 }}>
      {/* Gross Income / Revenue */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          {isSelfEmployed ? "Gross Revenue" : "Gross Income"}
        </Typography>
        <Typography variant="h6" fontWeight="bold" color="primary">
          {formatCurrency(grossIncome)}
        </Typography>
      </Box>

      {/* Business Deductions - Self-Employed Only */}
      {isSelfEmployed && periodBusinessDeductions > 0 && (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pl: 2,
              py: 0.5,
            }}
          >
            <Tooltip
              title="Business expenses you can deduct from your gross revenue before calculating taxes. Common deductions include home office, equipment, software, professional services, travel, and supplies."
              arrow
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  textDecoration: "underline",
                  textDecorationStyle: "dotted",
                  textUnderlineOffset: 3,
                  cursor: "help",
                }}
              >
                Business Deductions
              </Typography>
            </Tooltip>
            <Typography variant="body2" color="error.main">
              -{formatCurrency(periodBusinessDeductions)}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pl: 2,
              py: 0.5,
              mb: 1,
            }}
          >
            <Typography variant="body2" fontWeight="medium">
              Net Business Income
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {formatCurrency(periodNetBusinessIncome)}
            </Typography>
          </Box>
        </>
      )}

      {/* SE Tax Deduction - Self-Employed Only */}
      {isSelfEmployed && periodSETaxDeduction > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pl: 2,
            py: 0.5,
          }}
        >
          <Tooltip
            title="You can deduct half of your self-employment tax (7.65%) from your adjusted gross income. This accounts for the fact that employers normally pay half of FICA taxes for W-2 employees."
            arrow
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                textDecoration: "underline",
                textDecorationStyle: "dotted",
                textUnderlineOffset: 3,
                cursor: "help",
              }}
            >
              SE Tax Deduction (½ of SE Tax)
            </Typography>
          </Tooltip>
          <Typography variant="body2" color="error.main">
            -{formatCurrency(periodSETaxDeduction)}
          </Typography>
        </Box>
      )}

      {/* Standard Deduction */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pl: 2,
          py: 0.5,
        }}
      >
        <Tooltip
          title="The Standard Deduction is a flat dollar amount the IRS lets you subtract from your income instead of itemizing individual deductions (like mortgage interest, charitable donations, or medical expenses). About 88% of taxpayers choose this because it's simpler and often results in a larger deduction. You don't need receipts or proof—everyone who qualifies gets this amount based on their filing status."
          arrow
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              textDecoration: "underline",
              textDecorationStyle: "dotted",
              textUnderlineOffset: 3,
              cursor: "help",
            }}
          >
            Standard Deduction
          </Typography>
        </Tooltip>
        <Typography variant="body2" color="error.main">
          -{formatCurrency(periodStandardDeduction)}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          mt: 1,
        }}
      >
        <Typography variant="body1" fontWeight="medium">
          Taxable Income
        </Typography>
        <Typography variant="body1" fontWeight="medium">
          {formatCurrency(taxableIncome)}
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Taxes Header with Column Labels */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 80px 80px 110px",
          alignItems: "center",
          mb: 1,
          gap: 1,
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          2025 Taxes
        </Typography>
        <Tooltip
          title="Marginal rate is the tax rate applied to your next dollar of income in this tax bracket."
          arrow
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: "center", cursor: "help" }}
          >
            Marginal
          </Typography>
        </Tooltip>
        <Tooltip
          title="Effective rate is the average rate you actually pay, calculated as tax amount divided by gross income."
          arrow
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: "center", cursor: "help" }}
          >
            Effective
          </Typography>
        </Tooltip>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textAlign: "right" }}
        >
          Amount
        </Typography>
      </Box>

      {/* Federal Taxes Header */}
      <Typography variant="subtitle1" fontWeight="medium" sx={{ mt: 2, mb: 1 }}>
        {isSelfEmployed ? "Federal & Self-Employment Taxes" : "Federal Taxes"}
      </Typography>
      {federalTaxes.map((tax) => (
        <Box
          key={tax.name}
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 80px 80px 110px",
            alignItems: "center",
            pl: 2,
            py: 0.5,
            gap: 1,
          }}
        >
          {tax.tooltip ? (
            <Tooltip title={tax.tooltip} arrow>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  textDecoration: "underline",
                  textDecorationStyle: "dotted",
                  textUnderlineOffset: 3,
                  cursor: "help",
                }}
              >
                {tax.name}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {tax.name}
            </Typography>
          )}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center" }}
          >
            {tax.showRate ? formatPercent(tax.rate) : ""}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center" }}
          >
            {formatPercent(calcEffectiveRate(tax.amount))}
          </Typography>
          <Typography
            variant="body2"
            color="error.main"
            sx={{ textAlign: "right" }}
          >
            -{formatCurrency(tax.amount)}
          </Typography>
        </Box>
      ))}
      {/* Federal Taxes Subtotal */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 80px 80px 110px",
          alignItems: "center",
          pl: 2,
          py: 0.5,
          mt: 0.5,
          borderTop: 1,
          borderColor: "divider",
          gap: 1,
        }}
      >
        <Typography variant="body2" fontWeight="medium">
          {isSelfEmployed ? "Federal & SE Subtotal" : "Federal Subtotal"}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center" }}
        >
          {formatPercent(federalMarginalRate)}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center" }}
        >
          {formatPercent(calcEffectiveRate(totalFederalTaxes))}
        </Typography>
        <Typography
          variant="body2"
          fontWeight="medium"
          color="error.main"
          sx={{ textAlign: "right" }}
        >
          -{formatCurrency(totalFederalTaxes)}
        </Typography>
      </Box>

      {/* State Taxes Header */}
      <Typography variant="subtitle1" fontWeight="medium" sx={{ mt: 3, mb: 1 }}>
        {stateName} Taxes
      </Typography>
      {stateTaxes.map((tax) => (
        <Box
          key={tax.name}
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 80px 80px 110px",
            alignItems: "center",
            pl: 2,
            py: 0.5,
            gap: 1,
          }}
        >
          {tax.tooltip ? (
            <Tooltip title={tax.tooltip} arrow>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  textDecoration: "underline",
                  textDecorationStyle: "dotted",
                  textUnderlineOffset: 3,
                  cursor: "help",
                }}
              >
                {tax.name}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {tax.name}
            </Typography>
          )}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center" }}
          >
            {tax.showRate ? formatPercent(tax.rate) : ""}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center" }}
          >
            {formatPercent(calcEffectiveRate(tax.amount))}
          </Typography>
          <Typography
            variant="body2"
            color="error.main"
            sx={{ textAlign: "right" }}
          >
            -{formatCurrency(tax.amount)}
          </Typography>
        </Box>
      ))}
      {/* State Taxes Subtotal */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 80px 80px 110px",
          alignItems: "center",
          pl: 2,
          py: 0.5,
          mt: 0.5,
          borderTop: 1,
          borderColor: "divider",
          gap: 1,
        }}
      >
        <Typography variant="body2" fontWeight="medium">
          {stateName} Subtotal
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center" }}
        >
          {formatPercent(stateMarginalRate)}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center" }}
        >
          {formatPercent(calcEffectiveRate(totalStateTaxes))}
        </Typography>
        <Typography
          variant="body2"
          fontWeight="medium"
          color="error.main"
          sx={{ textAlign: "right" }}
        >
          -{formatCurrency(totalStateTaxes)}
        </Typography>
      </Box>

      {/* Credits Section */}
      {periodChildTaxCredit > 0 && (
        <>
          <Typography
            variant="subtitle1"
            fontWeight="medium"
            sx={{ mt: 3, mb: 1 }}
          >
            Credits
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 80px 80px 110px",
              alignItems: "center",
              pl: 2,
              py: 0.5,
              gap: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Child Tax Credit ({numChildren}{" "}
              {numChildren === 1 ? "child" : "children"})
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center" }}
            >
              —
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center" }}
            >
              —
            </Typography>
            <Typography
              variant="body2"
              color="success.main"
              sx={{ textAlign: "right" }}
            >
              +{formatCurrency(periodChildTaxCredit)}
            </Typography>
          </Box>
          {/* Total Credits */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 80px 80px 110px",
              alignItems: "center",
              pl: 2,
              py: 0.5,
              mt: 0.5,
              borderTop: 1,
              borderColor: "divider",
              gap: 1,
            }}
          >
            <Typography variant="body2" fontWeight="medium">
              Total Credits
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center" }}
            >
              —
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center" }}
            >
              —
            </Typography>
            <Typography
              variant="body2"
              fontWeight="medium"
              color="success.main"
              sx={{ textAlign: "right" }}
            >
              +{formatCurrency(periodChildTaxCredit)}
            </Typography>
          </Box>
        </>
      )}

      {/* Grand Total - Expandable */}
      <Box
        onClick={() =>
          totalTax > 0 && setPaymentPlanExpanded(!paymentPlanExpanded)
        }
        sx={{
          display: "grid",
          gridTemplateColumns: "auto 1fr 80px 80px 110px",
          alignItems: "center",
          mt: 3,
          pt: 1.5,
          pb: 1,
          borderTop: 2,
          borderColor: "primary.main",
          bgcolor: "action.hover",
          mx: -2,
          px: 2,
          gap: 1,
          cursor: totalTax > 0 ? "pointer" : "default",
          "&:hover": totalTax > 0 ? { bgcolor: "action.selected" } : {},
          transition: "background-color 0.2s",
        }}
      >
        {totalTax > 0 ? (
          paymentPlanExpanded ? (
            <ExpandLessIcon fontSize="small" color="action" />
          ) : (
            <ExpandMoreIcon fontSize="small" color="action" />
          )
        ) : (
          <Box sx={{ width: 20 }} />
        )}
        <Typography variant="subtitle1" fontWeight="bold">
          {totalTax < 0 ? (
            <Tooltip
              title="If your total tax is negative, it means credits exceed taxes owed (estimated refund). This can happen with credits like the Child Tax Credit."
              arrow
            >
              <span
                style={{
                  textDecoration: "underline",
                  textDecorationStyle: "dotted",
                  textUnderlineOffset: 3,
                  cursor: "help",
                }}
              >
                Net Tax Refund
              </span>
            </Tooltip>
          ) : (
            "Grand Total Tax"
          )}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center" }}
        >
          —
        </Typography>
        <Tooltip
          title="Your effective tax rate is the average rate you pay on all your income. It's calculated by dividing your total taxes by your gross income."
          arrow
        >
          <Typography
            variant="body2"
            color="primary"
            fontWeight="bold"
            sx={{ textAlign: "center", cursor: "help" }}
          >
            {formatPercent(effectiveTaxRate)}
          </Typography>
        </Tooltip>
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          color={totalTax < 0 ? "success.main" : "error.main"}
          sx={{ textAlign: "right" }}
        >
          {totalTax < 0
            ? `+${formatCurrency(Math.abs(totalTax))}`
            : `-${formatCurrency(totalTax)}`}
        </Typography>
      </Box>

      {/* Payment Plan Table */}
      <Collapse in={paymentPlanExpanded && totalTax > 0}>
        <Box
          sx={{
            mx: -2,
            px: 2,
            py: 2,
            bgcolor: "background.paper",
            borderLeft: 2,
            borderRight: 2,
            borderBottom: 2,
            borderColor: "primary.main",
          }}
        >
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            IRS Payment Plan Options
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 2 }}
          >
            If you can't pay your full tax bill, the IRS offers payment plans.
            Interest and penalties may apply.
          </Typography>
          <Table size="small">
            <TableBody>
              {[6, 12, 24, 36].map((months) => (
                <TableRow
                  key={months}
                  sx={{ "&:last-child td": { borderBottom: 0 } }}
                >
                  <TableCell sx={{ pl: 0, py: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {months} months
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ pr: 0, py: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(totalTax / months)}/mo
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            * Amounts shown do not include IRS interest (currently ~8% annually)
            or late payment penalties.
          </Typography>
        </Box>
      </Collapse>

      <Divider sx={{ my: 2 }} />

      {/* Take Home */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Take Home
        </Typography>
        <Box sx={{ textAlign: "right" }}>
          <Typography variant="h6" fontWeight="bold" color="success.main">
            {formatCurrency(netIncome)}
          </Typography>
          <Tooltip
            title="This is Take Home divided by Gross Income. It can be over 100% if your estimated credits/refund exceed taxes owed (negative total tax)."
            arrow
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ cursor: "help" }}
            >
              {formatPercent(grossIncome > 0 ? netIncome / grossIncome : 0)} of
              gross
            </Typography>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

// Main Tax Calculator Component
const TaxCalculator: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setShareHandler } = useShareAction();
  const shareId = searchParams.get("shareId");
  const isSharedView = Boolean(shareId);
  const [incomeInfo, setIncomeInfo] = useState<IncomeInfo>(() =>
    normalizeIncomeInfo({
      incomeSource: "w2",
      incomeSources: [
        {
          id: generateIncomeSourceId(),
          revenueSource: "",
          paymentType: "salary",
          paymentAmount: 0,
          hoursWorkedWeekly: 40,
        },
      ],
      businessDeductions: 0,
      state: "OR",
      filingStatus: "single",
      numChildren: 0,
    })
  );
  const [activeTab, setActiveTab] = useState(2); // Default to Yearly
  const standardDeductionAmount =
    STANDARD_DEDUCTIONS_2025[incomeInfo.filingStatus] ?? 0;

  // Profile management state
  const [profiles, setProfiles] = useState<TaxProfile[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState<string>("");
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [newProfileName, setNewProfileName] = useState("");
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
  const [unlockPassword, setUnlockPassword] = useState("");
  const [unlockError, setUnlockError] = useState("");
  const [pendingShareId, setPendingShareId] = useState<string | null>(null);

  const applySharedPayload = useCallback((payload: any) => {
    if (payload?.incomeInfo) {
      const normalized = normalizeIncomeInfo(payload.incomeInfo);
      console.debug(
        "[TaxCalculator] applySharedPayload incomeInfo",
        normalized
      );
      setIncomeInfo(normalized);
    }
    if (typeof payload?.activeTab === "number") {
      setActiveTab(payload.activeTab);
    }
  }, []);

  useEffect(() => {
    if (isSharedView) return;
    setShareHandler(() => setShareDialogOpen(true));
    return () => setShareHandler(null);
  }, [isSharedView, setShareHandler]);

  // Update URL when profile changes
  const updateUrlWithProfile = useCallback(
    (profileId: string) => {
      if (profileId) {
        setSearchParams({ profile: profileId });
      } else {
        setSearchParams({});
      }
    },
    [setSearchParams]
  );

  // Load profiles on mount and restore from URL
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (isSharedView) return;
      const remote = await taxProfilesDB.getAll();
      let normalizedProfiles = remote.map((profile) => ({
        ...profile,
        incomeInfo: normalizeIncomeInfo(profile.incomeInfo),
      }));

      // One-time migration: if remote is empty, import any legacy localStorage profiles and then remove them.
      if (normalizedProfiles.length === 0) {
        const legacy = loadTaxProfiles();
        if (legacy.length > 0) {
          console.debug(
            "[TaxCalculator] Migrating localStorage tax profiles to Supabase",
            { count: legacy.length }
          );
          const normalizedLegacy = legacy.map((profile) => ({
            ...profile,
            incomeInfo: normalizeIncomeInfo(profile.incomeInfo),
          }));
          await taxProfilesDB.upsertMany(normalizedLegacy);
          try {
            localStorage.removeItem(TAX_PROFILES_STORAGE_KEY);
          } catch {
            // ignore
          }
          const reloaded = await taxProfilesDB.getAll();
          normalizedProfiles = reloaded.map((profile) => ({
            ...profile,
            incomeInfo: normalizeIncomeInfo(profile.incomeInfo),
          }));
        }
      }

      if (!mounted) return;
      setProfiles(normalizedProfiles);

      // Check URL for profile ID
      const urlProfileId = searchParams.get("profile");
      if (urlProfileId) {
        const profile = normalizedProfiles.find((p) => p.id === urlProfileId);
        if (profile) {
          setIncomeInfo(profile.incomeInfo);
          setSelectedProfileId(urlProfileId);
        }
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!shareId) return;
    const loadShare = async () => {
      try {
        const info = await pageShareAPI.getInfo(shareId);
        if (info.pageType !== "tax") {
          setUnlockError("This share link is not for Tax Calculator.");
          return;
        }
        if (info.requiresPassword) {
          setPendingShareId(shareId);
          setUnlockDialogOpen(true);
          return;
        }
        const result = await pageShareAPI.validate(shareId);
        applySharedPayload(result.payload);
      } catch (error) {
        console.error("[TaxCalculator] Failed to load share", error);
        setUnlockError("Failed to load shared data.");
      }
    };
    loadShare();
  }, [applySharedPayload, shareId]);

  // Persist changes to the selected profile automatically (debounced).
  // This keeps profiles DB-backed rather than "only in the browser".
  useEffect(() => {
    if (isSharedView) return;
    if (!selectedProfileId) return;

    const profile = profiles.find((p) => p.id === selectedProfileId);
    if (!profile) return;

    const timer = window.setTimeout(async () => {
      const nextProfile: TaxProfile = {
        ...profile,
        incomeInfo: { ...incomeInfo },
      };
      setProfiles((prev) =>
        prev.map((p) => (p.id === nextProfile.id ? nextProfile : p))
      );
      await taxProfilesDB.upsert(nextProfile);
    }, 750);

    return () => window.clearTimeout(timer);
  }, [incomeInfo, isSharedView, profiles, selectedProfileId]);

  // Save profile handler
  const handleSaveProfile = async () => {
    if (!newProfileName.trim()) return;

    const newProfile: TaxProfile = {
      id: generateTaxProfileId(),
      name: newProfileName.trim(),
      incomeInfo: { ...incomeInfo },
      createdAt: Date.now(),
    };

    const saved = await taxProfilesDB.upsert(newProfile);
    if (!saved) return;
    const updatedProfiles = [...profiles, saved];
    setProfiles(updatedProfiles);
    setSelectedProfileId(saved.id);
    updateUrlWithProfile(saved.id);
    setNewProfileName("");
    setSaveDialogOpen(false);
  };

  // Load profile handler
  const handleLoadProfile = (profileId: string) => {
    if (!profileId) {
      setSelectedProfileId("");
      updateUrlWithProfile("");
      return;
    }

    const profile = profiles.find((p) => p.id === profileId);
    if (profile) {
      setIncomeInfo(profile.incomeInfo);
      setSelectedProfileId(profileId);
      updateUrlWithProfile(profileId);
    }
  };

  const handleUnlockShare = async () => {
    if (!pendingShareId || !unlockPassword.trim()) {
      setUnlockError("Password required.");
      return;
    }
    setUnlockError("");
    try {
      const result = await pageShareAPI.validate(
        pendingShareId,
        unlockPassword.trim()
      );
      applySharedPayload(result.payload);
      setUnlockDialogOpen(false);
      setPendingShareId(null);
      setUnlockPassword("");
    } catch (error) {
      console.error("[TaxCalculator] Failed to unlock share", error);
      setUnlockError("Invalid password.");
    }
  };

  // Delete profile handler
  const handleDeleteProfile = async (profileId: string) => {
    await taxProfilesDB.delete(profileId);
    const updatedProfiles = profiles.filter((p) => p.id !== profileId);
    setProfiles(updatedProfiles);
    if (selectedProfileId === profileId) {
      setSelectedProfileId("");
      updateUrlWithProfile("");
    }
  };

  // Update profile when income info changes (if a profile is selected)
  const handleUpdateProfile = async () => {
    if (!selectedProfileId) return;

    const updatedProfiles = profiles.map((p) =>
      p.id === selectedProfileId ? { ...p, incomeInfo: { ...incomeInfo } } : p
    );
    setProfiles(updatedProfiles);
    const nextProfile = updatedProfiles.find((p) => p.id === selectedProfileId);
    if (nextProfile) {
      await taxProfilesDB.upsert(nextProfile);
    }
  };

  // Auto-calculate whenever inputs change
  const incomeDetails = useMemo(() => {
    const hasValidIncomeSource = incomeInfo.incomeSources.some((source) => {
      if (source.paymentType === "hourly") {
        return source.paymentAmount > 0 && source.hoursWorkedWeekly > 0;
      }
      return source.paymentAmount > 0;
    });
    if (!hasValidIncomeSource) return null;
    return calculateIncomeDetails(incomeInfo);
  }, [incomeInfo]);

  const getActiveDetails = (): IncomeDetails | null => {
    if (!incomeDetails) return null;
    switch (activeTab) {
      case 0:
        return incomeDetails.weeklyIncome;
      case 1:
        return incomeDetails.monthlyIncome;
      case 2:
        return incomeDetails.yearlyIncome;
      default:
        return incomeDetails.weeklyIncome;
    }
  };

  const getPeriod = (): "weekly" | "monthly" | "yearly" => {
    switch (activeTab) {
      case 0:
        return "weekly";
      case 1:
        return "monthly";
      case 2:
        return "yearly";
      default:
        return "yearly";
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Income Tax Calculator
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Calculate your take-home pay after federal and state taxes, including
        deductions and credits.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        This calculator uses 2025 federal and state tax brackets, standard
        deductions, and the Child Tax Credit ($2,200/child). Results are
        estimates and should not be used for tax filing.
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption">
            Sources:{" "}
            <Link
              href="https://taxfoundation.org/data/all/federal/2025-tax-brackets/"
              target="_blank"
              rel="noopener"
            >
              Tax Foundation
            </Link>
            {", "}
            <Link
              href="https://www.irs.gov/newsroom/irs-releases-tax-inflation-adjustments-for-tax-year-2026-including-amendments-from-the-one-big-beautiful-bill"
              target="_blank"
              rel="noopener"
            >
              IRS (standard deduction)
            </Link>
            {", "}
            <Link
              href="https://www.irs.gov/credits-deductions/individuals/child-tax-credit"
              target="_blank"
              rel="noopener"
            >
              IRS
            </Link>
          </Typography>
        </Box>
      </Alert>

      {/* Tax Profiles */}
      {isSharedView ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          Shared view: changes are editable but will not be saved to your
          profiles.
        </Alert>
      ) : (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            flexWrap="wrap"
          >
            <PersonIcon color="primary" />
            <Typography
              variant="subtitle1"
              fontWeight="medium"
              sx={{ minWidth: 80 }}
            >
              Profiles
            </Typography>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Select Profile</InputLabel>
              <Select
                value={selectedProfileId}
                label="Select Profile"
                onChange={(e) => handleLoadProfile(e.target.value as string)}
              >
                <MenuItem value="">
                  <em>New Profile</em>
                </MenuItem>
                {profiles.map((profile) => (
                  <MenuItem key={profile.id} value={profile.id}>
                    {profile.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={() => setSaveDialogOpen(true)}
              size="small"
            >
              Save As
            </Button>

            {selectedProfileId && (
              <>
                <Button
                  variant="outlined"
                  onClick={handleUpdateProfile}
                  size="small"
                >
                  Update
                </Button>
                <Tooltip title="Delete this profile">
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteProfile(selectedProfileId)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}

            {profiles.length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: "auto" }}
              >
                {profiles.length} saved profile
                {profiles.length !== 1 ? "s" : ""}
              </Typography>
            )}
          </Stack>
        </Paper>
      )}

      {/* Save Profile Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
        <DialogTitle>Save Tax Profile</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Profile Name"
            fullWidth
            variant="outlined"
            value={newProfileName}
            onChange={(e) => setNewProfileName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && newProfileName.trim()) {
                handleSaveProfile();
              }
            }}
            placeholder="e.g., My Full-Time Job, Side Gig, etc."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveProfile}
            variant="contained"
            disabled={!newProfileName.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Paper sx={{ p: 3, mb: 3 }}>
        {/* Income Source Toggle */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Income Source
          </Typography>
          <Stack direction="row" spacing={1}>
            <Chip
              label="W-2 Employee"
              onClick={() =>
                setIncomeInfo((prev) => ({ ...prev, incomeSource: "w2" }))
              }
              color={incomeInfo.incomeSource === "w2" ? "primary" : "default"}
              variant={incomeInfo.incomeSource === "w2" ? "filled" : "outlined"}
              sx={{ cursor: "pointer" }}
            />
            <Chip
              label="Self-Employed / LLC"
              onClick={() =>
                setIncomeInfo((prev) => ({
                  ...prev,
                  incomeSource: "self_employed",
                }))
              }
              color={
                incomeInfo.incomeSource === "self_employed"
                  ? "primary"
                  : "default"
              }
              variant={
                incomeInfo.incomeSource === "self_employed"
                  ? "filled"
                  : "outlined"
              }
              sx={{ cursor: "pointer" }}
            />
          </Stack>
          {incomeInfo.incomeSource === "self_employed" && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Self-Employment Tax:</strong> As a single-member LLC,
                you pay both the employee and employer portions of Social
                Security (12.4%) and Medicare (2.9%) taxes — totaling 15.3% on
                your net business income.
              </Typography>
            </Alert>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Filing Information
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1fr 1fr 1fr 1fr",
            },
            gap: 3,
            mb: 3,
          }}
        >
          <FormControl fullWidth>
            <InputLabel>Filing Status</InputLabel>
            <Select
              value={incomeInfo.filingStatus}
              label="Filing Status"
              onChange={(e) => {
                setIncomeInfo((prev) => ({
                  ...prev,
                  filingStatus: e.target.value as FilingStatus,
                }));
              }}
            >
              <MenuItem value="single">Single</MenuItem>
              <MenuItem value="married_jointly">
                Married Filing Jointly
              </MenuItem>
              <MenuItem value="head_of_household">Head of Household</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>State</InputLabel>
            <Select
              value={incomeInfo.state}
              label="State"
              onChange={(e) => {
                setIncomeInfo((prev) => ({
                  ...prev,
                  state: e.target.value as StateCode,
                }));
              }}
            >
              <MenuItem value="OR">Oregon</MenuItem>
              <MenuItem value="NY">New York</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Children Under 17"
            type="number"
            value={incomeInfo.numChildren}
            onChange={(e) => {
              setIncomeInfo((prev) => ({
                ...prev,
                numChildren: Math.max(0, parseInt(e.target.value) || 0),
              }));
            }}
            inputProps={{ min: 0, max: 20 }}
            helperText="For Child Tax Credit"
          />

          <Box>
            <Tooltip
              arrow
              title={
                <Box sx={{ maxWidth: 360 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Standard Deduction (2025)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    A single flat deduction set by the IRS for your filing
                    status. It is not made of line items. This calculator does
                    not model itemized deductions.
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2">
                    Standard Deduction:{" "}
                    <strong>{formatCurrency(standardDeductionAmount)}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Additional Standard Deduction (age/blindness):{" "}
                    <strong>{formatCurrency(0)}</strong> (not modeled)
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Itemized deductions are not included in this calculator.
                    Current itemized totals:
                  </Typography>
                  <Typography variant="body2">
                    Mortgage interest: <strong>{formatCurrency(0)}</strong>
                  </Typography>
                  <Typography variant="body2">
                    State and local taxes (SALT):{" "}
                    <strong>{formatCurrency(0)}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Charitable contributions:{" "}
                    <strong>{formatCurrency(0)}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Medical expenses: <strong>{formatCurrency(0)}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Other itemized deductions:{" "}
                    <strong>{formatCurrency(0)}</strong>
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    Source: IRS standard deduction amounts for tax year 2025
                    (under OBBB, as published Oct 9, 2025 in IR-2025-103).
                  </Typography>
                </Box>
              }
            >
              <Box
                sx={{
                  cursor: "help",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 0.5 }}
                >
                  Standard Deduction
                </Typography>
                <Typography variant="h6" color="primary">
                  {formatCurrency(standardDeductionAmount)}
                </Typography>
              </Box>
            </Tooltip>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          {incomeInfo.incomeSource === "self_employed"
            ? "Business Income"
            : "Income"}
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Typography variant="body2" color="text.secondary">
            Add multiple income items. The calculator sums them.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const nextSource: IncomeSourceEntry = {
                id: generateIncomeSourceId(),
                revenueSource: "",
                paymentType: "salary",
                paymentAmount: 0,
                hoursWorkedWeekly: 40,
              };
              setIncomeInfo((prev) => ({
                ...prev,
                incomeSources: [...prev.incomeSources, nextSource],
              }));
            }}
          >
            Add Income Item
          </Button>
        </Stack>

        <Stack spacing={2}>
          {incomeInfo.incomeSources.map((source, idx) => {
            const isHourly = source.paymentType === "hourly";
            const amountLabel =
              incomeInfo.incomeSource === "self_employed"
                ? isHourly
                  ? "Hourly Rate"
                  : "Annual Revenue"
                : isHourly
                  ? "Hourly Rate"
                  : "Annual Salary";

            const revenueSourceValue = source.revenueSource ?? "";
            const paymentTypeValue =
              source.paymentType === "hourly" ? "hourly" : "salary";
            const paymentAmountValue =
              typeof source.paymentAmount === "number" &&
              Number.isFinite(source.paymentAmount)
                ? source.paymentAmount
                : 0;
            const hoursWorkedWeeklyValue =
              typeof source.hoursWorkedWeekly === "number" &&
              Number.isFinite(source.hoursWorkedWeekly)
                ? source.hoursWorkedWeekly
                : 40;

            return (
              <Box
                key={source.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "2fr 1fr 1fr 1fr auto",
                  },
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <TextField
                  fullWidth
                  label={idx === 0 ? "Source" : "Source (Optional)"}
                  value={revenueSourceValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    setIncomeInfo((prev) => ({
                      ...prev,
                      incomeSources: prev.incomeSources.map((s) =>
                        s.id === source.id ? { ...s, revenueSource: value } : s
                      ),
                    }));
                  }}
                />

                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={paymentTypeValue}
                    label="Type"
                    onChange={(e) => {
                      const value = e.target.value as "hourly" | "salary";
                      setIncomeInfo((prev) => ({
                        ...prev,
                        incomeSources: prev.incomeSources.map((s) =>
                          s.id === source.id
                            ? {
                                ...s,
                                paymentType: value,
                                hoursWorkedWeekly:
                                  value === "hourly" ? s.hoursWorkedWeekly : 40,
                              }
                            : s
                        ),
                      }));
                    }}
                  >
                    <MenuItem value="hourly">Hourly</MenuItem>
                    <MenuItem value="salary">Salary</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label={amountLabel}
                  type="number"
                  value={paymentAmountValue}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    setIncomeInfo((prev) => ({
                      ...prev,
                      incomeSources: prev.incomeSources.map((s) =>
                        s.id === source.id ? { ...s, paymentAmount: value } : s
                      ),
                    }));
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  inputProps={{ min: 0, step: 100 }}
                />

                <TextField
                  fullWidth
                  label={isHourly ? "Hours/Week" : "Hours/Week"}
                  type="number"
                  disabled={!isHourly}
                  value={hoursWorkedWeeklyValue}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    setIncomeInfo((prev) => ({
                      ...prev,
                      incomeSources: prev.incomeSources.map((s) =>
                        s.id === source.id
                          ? { ...s, hoursWorkedWeekly: value }
                          : s
                      ),
                    }));
                  }}
                  inputProps={{ min: 0, max: 168, step: 0.5 }}
                />

                <Tooltip title="Remove item">
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={incomeInfo.incomeSources.length <= 1}
                      onClick={() => {
                        setIncomeInfo((prev) => ({
                          ...prev,
                          incomeSources: prev.incomeSources.filter(
                            (s) => s.id !== source.id
                          ),
                        }));
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            );
          })}
        </Stack>

        {/* Business Deductions - Only for Self-Employed */}
        {incomeInfo.incomeSource === "self_employed" && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>
              Business Deductions
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter your estimated annual business expenses. These are deducted
              from your gross revenue before calculating self-employment tax.
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 3,
              }}
            >
              <TextField
                fullWidth
                label="Annual Business Expenses"
                type="number"
                value={incomeInfo.businessDeductions}
                onChange={(e) => {
                  setIncomeInfo((prev) => ({
                    ...prev,
                    businessDeductions: parseFloat(e.target.value) || 0,
                  }));
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                inputProps={{ min: 0, step: 100 }}
                helperText="e.g., home office, equipment, software, travel, supplies"
              />
              {(incomeDetails?.yearlyIncome.grossIncome ?? 0) > 0 &&
                incomeInfo.businessDeductions > 0 && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 0.5 }}
                    >
                      Net Business Income
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {formatCurrency(
                        incomeDetails?.yearlyIncome.netBusinessIncome ?? 0
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Gross Revenue - Business Expenses
                    </Typography>
                  </Box>
                )}
            </Box>
          </>
        )}
      </Paper>

      {/* Results */}
      {incomeDetails && (
        <Paper sx={{ p: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
          >
            <Tab label="Weekly" />
            <Tab label="Monthly" />
            <Tab label="Yearly" />
          </Tabs>

          {getActiveDetails() && (
            <ResultsTab
              details={getActiveDetails()!}
              stateName={STATE_NAMES[incomeInfo.state]}
              numChildren={incomeInfo.numChildren}
              period={getPeriod()}
            />
          )}
        </Paper>
      )}

      <PageShareModal
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        pageType="tax"
        payload={{
          version: 1,
          incomeInfo,
          activeTab,
        }}
        label={
          profiles.find((profile) => profile.id === selectedProfileId)?.name ||
          "Tax Calculator"
        }
        buildShareUrl={(shareId) =>
          `${window.location.origin}/shared/tax-calculator?shareId=${shareId}`
        }
      />

      <Dialog
        open={unlockDialogOpen}
        onClose={() => setUnlockDialogOpen(false)}
      >
        <DialogTitle>Enter Password</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            value={unlockPassword}
            onChange={(e) => setUnlockPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleUnlockShare();
              }
            }}
          />
          {unlockError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {unlockError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlockDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUnlockShare}>
            Unlock
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaxCalculator;
